import { useState, useEffect } from 'react'
import { Settings } from 'lucide-react'
import { parkingConfigurationApi } from '../services/api'
import { showError } from '../utils/showError'
import {
  Card,
  CardHeader,
  CardBody,
  LoadingSpinner,
  Button,
  FormField,
  FormSelect,
  FormTextarea,
  FormCheckbox,
  SuccessMessage
} from '../components/common'

const PARKING_TYPES = [
  { value: 1, label: 'Specific Spot' },
  { value: 2, label: 'Limited Open' },
  { value: 3, label: 'Free Parking' },
]

const ParkingConfiguration = ({ buildingId }) => {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState('')
  const [formData, setFormData] = useState({
    parkingType: 1,
    totalCapacity: '',
    maxActiveReservationsPerPlate: '',
    rules: '',
    startTime: '',
    endTime: '',
    isAvailable24_7: false,
    requiresAdvanceReservation: true,
    checkInExpiryHours: '',
    maxReservationsPerResidentWeekly: '',
    maxReservationsPerResidentMonthly: '',
    maxActiveReservationsPerUnit: '',
    maxGuestVisitsPerPlateWeekly: '',
    maxGuestVisitsPerPlateMonthly: '',
    maxReservationHours: '',
    reservationWindowDays: '',
    isSetupCompleted: false,
  })

  useEffect(() => {
    if (buildingId) {
      loadConfig()
    }
  }, [buildingId])

  const loadConfig = async () => {
    try {
      setLoading(true)
      const response = await parkingConfigurationApi.get(buildingId)
      const data = response.data?.data || response.data
      if (data && data.parkingConfigurationId) {
        setFormData({
          parkingType: data.parkingType || 1,
          totalCapacity: data.totalCapacity ?? '',
          maxActiveReservationsPerPlate: data.maxActiveReservationsPerPlate ?? '',
          rules: data.rules || '',
          startTime: data.startTime ? data.startTime.substring(0, 5) : '',
          endTime: data.endTime ? data.endTime.substring(0, 5) : '',
          isAvailable24_7: data.isAvailable24_7 || false,
          requiresAdvanceReservation: data.requiresAdvanceReservation ?? true,
          checkInExpiryHours: data.checkInExpiryHours ?? '',
          maxReservationsPerResidentWeekly: data.maxReservationsPerResidentWeekly ?? '',
          maxReservationsPerResidentMonthly: data.maxReservationsPerResidentMonthly ?? '',
          maxActiveReservationsPerUnit: data.maxActiveReservationsPerUnit ?? '',
          maxGuestVisitsPerPlateWeekly: data.maxGuestVisitsPerPlateWeekly ?? '',
          maxGuestVisitsPerPlateMonthly: data.maxGuestVisitsPerPlateMonthly ?? '',
          maxReservationHours: data.maxReservationHours ?? '',
          reservationWindowDays: data.reservationWindowDays ?? '',
          isSetupCompleted: data.isSetupCompleted ?? false,
        })
      } else {
        // No config yet, reset to defaults
        setFormData({
          parkingType: 1,
          totalCapacity: '',
          maxActiveReservationsPerPlate: '',
          rules: '',
          startTime: '',
          endTime: '',
          isAvailable24_7: false,
          requiresAdvanceReservation: true,
          checkInExpiryHours: '',
          maxReservationsPerResidentWeekly: '',
          maxReservationsPerResidentMonthly: '',
          maxActiveReservationsPerUnit: '',
          maxGuestVisitsPerPlateWeekly: '',
          maxGuestVisitsPerPlateMonthly: '',
          maxReservationHours: '',
          reservationWindowDays: '',
          isSetupCompleted: false,
        })
      }
    } catch (err) {
      console.error('Error loading config:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setSuccess('')
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      setSuccess('')

      const toIntOrNull = (val) => {
        const num = parseInt(val)
        return isNaN(num) ? null : num
      }

      await parkingConfigurationApi.set({
        buildingId,
        parkingType: parseInt(formData.parkingType),
        totalCapacity: toIntOrNull(formData.totalCapacity),
        maxActiveReservationsPerPlate: toIntOrNull(formData.maxActiveReservationsPerPlate),
        rules: formData.rules || null,
        startTime: formData.isAvailable24_7 ? null : (formData.startTime || null),
        endTime: formData.isAvailable24_7 ? null : (formData.endTime || null),
        isAvailable24_7: formData.isAvailable24_7,
        requiresAdvanceReservation: formData.requiresAdvanceReservation,
        checkInExpiryHours: toIntOrNull(formData.checkInExpiryHours),
        maxReservationsPerResidentWeekly: toIntOrNull(formData.maxReservationsPerResidentWeekly),
        maxReservationsPerResidentMonthly: toIntOrNull(formData.maxReservationsPerResidentMonthly),
        maxActiveReservationsPerUnit: toIntOrNull(formData.maxActiveReservationsPerUnit),
        maxGuestVisitsPerPlateWeekly: toIntOrNull(formData.maxGuestVisitsPerPlateWeekly),
        maxGuestVisitsPerPlateMonthly: toIntOrNull(formData.maxGuestVisitsPerPlateMonthly),
        maxReservationHours: toIntOrNull(formData.maxReservationHours),
        reservationWindowDays: toIntOrNull(formData.reservationWindowDays),
        isSetupCompleted: formData.isSetupCompleted,
      })

      setSuccess('Parking configuration saved successfully')
    } catch (err) {
      showError(err)
      console.error('Error saving config:', err)
    } finally {
      setSubmitting(false)
    }
  }

  if (!buildingId) return null

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-4">
      {success && <SuccessMessage message={success} />}

      <Card>
        <CardHeader title="Parking Type & Capacity" />
        <CardBody className="space-y-6">
          <FormSelect
            label="Parking Type"
            value={formData.parkingType}
            onChange={(e) => handleInputChange('parkingType', e.target.value)}
            options={PARKING_TYPES.map(t => ({ value: t.value, label: t.label }))}
          />

          <FormField
            label="Total Capacity"
            type="number"
            value={formData.totalCapacity}
            onChange={(e) => handleInputChange('totalCapacity', e.target.value)}
            placeholder="Total parking capacity"
            helpText="Leave empty if unlimited"
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Operating Hours" />
        <CardBody className="space-y-6">
          <FormCheckbox
            label="Available 24/7"
            checked={formData.isAvailable24_7}
            onChange={(e) => handleInputChange('isAvailable24_7', e.target.checked)}
          />

          {!formData.isAvailable24_7 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Start Time"
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
              />
              <FormField
                label="End Time"
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
              />
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Reservation Settings" />
        <CardBody className="space-y-6">
          <FormCheckbox
            label="Requires Advance Reservation"
            checked={formData.requiresAdvanceReservation}
            onChange={(e) => handleInputChange('requiresAdvanceReservation', e.target.checked)}
          />

          <FormField
            label="Check-In Expiry (hours)"
            type="number"
            value={formData.checkInExpiryHours}
            onChange={(e) => handleInputChange('checkInExpiryHours', e.target.value)}
            placeholder="Hours before reservation expires without check-in"
            helpText="Leave empty to disable"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Max Active Reservations Per Plate"
              type="number"
              value={formData.maxActiveReservationsPerPlate}
              onChange={(e) => handleInputChange('maxActiveReservationsPerPlate', e.target.value)}
              placeholder="Leave empty for unlimited"
            />
            <FormField
              label="Max Active Reservations Per Unit"
              type="number"
              value={formData.maxActiveReservationsPerUnit}
              onChange={(e) => handleInputChange('maxActiveReservationsPerUnit', e.target.value)}
              placeholder="Leave empty for unlimited"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Max Reservations Per Resident (Weekly)"
              type="number"
              value={formData.maxReservationsPerResidentWeekly}
              onChange={(e) => handleInputChange('maxReservationsPerResidentWeekly', e.target.value)}
              placeholder="Leave empty for unlimited"
            />
            <FormField
              label="Max Reservations Per Resident (Monthly)"
              type="number"
              value={formData.maxReservationsPerResidentMonthly}
              onChange={(e) => handleInputChange('maxReservationsPerResidentMonthly', e.target.value)}
              placeholder="Leave empty for unlimited"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Max Guest Visits Per Plate (Weekly)"
              type="number"
              value={formData.maxGuestVisitsPerPlateWeekly}
              onChange={(e) => handleInputChange('maxGuestVisitsPerPlateWeekly', e.target.value)}
              placeholder="Leave empty for unlimited"
            />
            <FormField
              label="Max Guest Visits Per Plate (Monthly)"
              type="number"
              value={formData.maxGuestVisitsPerPlateMonthly}
              onChange={(e) => handleInputChange('maxGuestVisitsPerPlateMonthly', e.target.value)}
              placeholder="Leave empty for unlimited"
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Rules" />
        <CardBody className="space-y-6">
          <FormTextarea
            label="Parking Rules"
            value={formData.rules}
            onChange={(e) => handleInputChange('rules', e.target.value)}
            placeholder="Enter parking rules and guidelines for residents..."
            rows={4}
          />
        </CardBody>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          variant="primary"
          loading={submitting}
          icon={<Settings className="w-4 h-4" />}
        >
          {submitting ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
    </div>
  )
}

export default ParkingConfiguration
